import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, ChevronRight, MessageCircle, IndianRupee, Building, TrendingUp } from "lucide-react";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20checked%20my%20business%20loan%20eligibility%20and%20want%20to%20apply";

const BUSINESS_TYPES = [
  { value: "PROPRIETORSHIP", label: "Proprietorship" },
  { value: "PARTNERSHIP", label: "Partnership" },
  { value: "PVT_LTD", label: "Private Limited" },
  { value: "MSME", label: "MSME" },
];

const formatINR = (num) => Math.round(num || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const formatLakhCr = (num) => {
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  return `₹${formatINR(num)}`;
};

const LoanEligibilityCalculatorPage = () => {
  const [businessType, setBusinessType] = useState("MSME");
  const [turnover, setTurnover] = useState(5000000);
  const [years, setYears] = useState(3);
  const [cibil, setCibil] = useState(750);
  const [existingEmi, setExistingEmi] = useState(10000);
  const [monthlyProfit, setMonthlyProfit] = useState(100000);

  useCanonical("/loan-eligibility-calculator");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Business Loan Eligibility Calculator",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "All",
    "description": "Check business loan eligibility in Coimbatore based on turnover, CIBIL score, vintage and existing EMI obligations.",
    "url": "https://wintrustsolutions.com/loan-eligibility-calculator",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
  });

  const results = useMemo(() => {
    const t = Math.max(0, Number(turnover) || 0);
    const yrs = Math.max(0, Number(years) || 0);
    const c = Math.max(300, Math.min(900, Number(cibil) || 300));
    const emi = Math.max(0, Number(existingEmi) || 0);
    const profit = Math.max(0, Number(monthlyProfit) || 0);

    const factors = [];

    if (yrs < 1) {
      return {
        eligible: false,
        message: "Minimum 1 year of operations required",
        maxEligibility: 0,
        minEligibility: 0,
        loanType: "—",
        interestRange: "—",
        tenure: "—",
        factors: ["Business vintage below 1 year — not eligible under standard lending norms."],
      };
    }

    if (c < 650) {
      return {
        eligible: false,
        message: "Improve CIBIL score before applying",
        maxEligibility: 0,
        minEligibility: 0,
        loanType: "—",
        interestRange: "—",
        tenure: "—",
        factors: [
          "CIBIL score below 650 — most banks/NBFCs will reject.",
          "Clear existing dues and maintain timely payments for 6–12 months to improve score.",
        ],
      };
    }

    let pct = 0;
    let interestRange = "";
    if (c >= 750) {
      pct = 0.20;
      interestRange = "10.5% – 14%";
      factors.push("CIBIL 750+ — eligible for best interest rates.");
    } else if (c >= 700) {
      pct = 0.15;
      interestRange = "13% – 16%";
      factors.push("CIBIL 700–749 — good score, standard rates.");
    } else {
      pct = 0.10;
      interestRange = "16% – 20%";
      factors.push("CIBIL 650–699 — higher rate bracket, limited lenders.");
    }

    let eligibility = t * pct;

    if (profit > 0 && emi > 0.5 * profit) {
      eligibility = eligibility * 0.7;
      factors.push("Existing EMIs exceed 50% of monthly profit — eligibility reduced by 30%.");
    }

    // MSME cap
    if (businessType === "MSME" && eligibility > 20000000) {
      eligibility = 20000000;
      factors.push("Capped at ₹2 Crore MSME lending limit.");
    }

    const minEligibility = eligibility * 0.8;
    const maxEligibility = eligibility;

    let loanType = "Business Term Loan";
    if (businessType === "MSME") loanType = "MSME Loan / CGTMSE backed";
    if (businessType === "PROPRIETORSHIP" && t < 2000000) loanType = "Working Capital / Micro Loan";

    const tenure = eligibility > 5000000 ? "3 – 7 years" : "1 – 5 years";

    factors.push(`Business vintage: ${yrs} year(s).`);
    factors.push(`Annual turnover: ${formatLakhCr(t)}.`);

    return {
      eligible: true,
      message: null,
      minEligibility,
      maxEligibility,
      loanType,
      interestRange,
      tenure,
      factors,
    };
  }, [businessType, turnover, years, cibil, existingEmi, monthlyProfit]);

  return (
    <>
      <Helmet>
        <title>Business Loan Eligibility Calculator — Coimbatore | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Free business loan eligibility calculator. Check loan amount based on turnover, CIBIL score and business vintage. Get MSME loan assistance in Coimbatore." />
      </Helmet>

      <section className="bg-navy py-12 md:py-16">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">Loan Eligibility Calculator</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-4" data-testid="loan-eligibility-h1">
            Business Loan Eligibility Calculator — Coimbatore
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            Estimate your business loan eligibility instantly based on turnover, CIBIL score, vintage and existing EMI obligations. Tailored for MSMEs and growing businesses in Tamil Nadu.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10">
            <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200 shadow-sm">
              <h2 className="text-2xl font-semibold text-navy mb-6 font-heading">Enter Business Details</h2>

              <div className="mb-5">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <Building size={16} /> Business Type
                </label>
                <select
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="business-type-select"
                >
                  {BUSINESS_TYPES.map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <IndianRupee size={16} /> Annual Turnover (₹)
                </label>
                <input
                  type="number"
                  min={0}
                  value={turnover}
                  onChange={(e) => setTurnover(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="turnover-input"
                />
              </div>

              <div className="mb-5">
                <label className="text-navy font-semibold mb-2">Years in Business</label>
                <input
                  type="number"
                  min={0}
                  value={years}
                  onChange={(e) => setYears(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="years-input"
                />
              </div>

              <div className="mb-5">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <TrendingUp size={16} /> CIBIL Score (300–900)
                </label>
                <input
                  type="number"
                  min={300}
                  max={900}
                  value={cibil}
                  onChange={(e) => setCibil(Math.max(300, Math.min(900, Number(e.target.value))))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="cibil-input"
                />
              </div>

              <div className="mb-5">
                <label className="text-navy font-semibold mb-2">Existing Monthly EMI Obligations (₹)</label>
                <input
                  type="number"
                  min={0}
                  value={existingEmi}
                  onChange={(e) => setExistingEmi(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="existing-emi-input"
                />
              </div>

              <div>
                <label className="text-navy font-semibold mb-2">Monthly Net Profit (₹)</label>
                <input
                  type="number"
                  min={0}
                  value={monthlyProfit}
                  onChange={(e) => setMonthlyProfit(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="monthly-profit-input"
                />
              </div>
            </div>

            <div className="space-y-4" data-testid="loan-eligibility-results">
              {!results.eligible ? (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6 md:p-8">
                  <p className="text-red-900 font-semibold text-lg mb-3" data-testid="not-eligible-message">
                    {results.message}
                  </p>
                  <ul className="text-slate-700 text-sm space-y-2 list-disc pl-5">
                    {results.factors.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </div>
              ) : (
                <>
                  <div className="bg-navy rounded-xl p-6 md:p-8 text-center">
                    <p className="text-slate-400 text-sm mb-1">Estimated Loan Eligibility</p>
                    <p className="text-3xl md:text-4xl font-bold text-gold font-heading" data-testid="eligibility-range">
                      {formatLakhCr(results.minEligibility)} – {formatLakhCr(results.maxEligibility)}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-5 border border-slate-200">
                      <p className="text-slate-500 text-xs mb-1">Recommended Loan Type</p>
                      <p className="text-sm font-bold text-navy" data-testid="loan-type">{results.loanType}</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-slate-200">
                      <p className="text-slate-500 text-xs mb-1">Interest Rate Range</p>
                      <p className="text-sm font-bold text-navy" data-testid="interest-range">{results.interestRange}</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-slate-200 col-span-2">
                      <p className="text-slate-500 text-xs mb-1">Suggested Tenure</p>
                      <p className="text-sm font-bold text-navy" data-testid="tenure-suggestion">{results.tenure}</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <p className="text-navy font-semibold mb-3 text-sm">Key factors affecting eligibility</p>
                    <ul className="text-slate-600 text-xs space-y-1.5 list-disc pl-5" data-testid="factors-list">
                      {results.factors.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="mt-10 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 md:p-8" data-testid="amber-alert">
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={28} />
              <div className="flex-1">
                <p className="text-amber-900 font-semibold text-base md:text-lg mb-4">
                  Ready to apply? Our team handles the entire process — documentation, bank coordination, and follow-up.
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp"
                    data-testid="cta-whatsapp-btn"
                  >
                    <MessageCircle size={18} /> Apply for Loan — WhatsApp Us
                  </a>
                  <Link
                    to="/business-loan-assistance"
                    className="text-navy font-semibold hover:text-gold transition-colors flex items-center gap-1"
                    data-testid="link-business-loan"
                  >
                    View Business Loan Assistance <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-8 text-xs text-slate-500 max-w-3xl">
            <strong>Disclaimer:</strong> Eligibility is indicative based on general lending criteria. Actual loan amount depends on lender assessment and credit policy.
          </p>
        </div>
      </section>
    </>
  );
};

export default LoanEligibilityCalculatorPage;
